import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  map, Observable, switchMap, exhaustMap, interval, takeWhile, of, BehaviorSubject, tap, shareReplay,
  filter, throwError, catchError
} from 'rxjs';
import {
  DocumentTemplate, DocumentTemplateAddDTO, DocumentTemplateField, DocumentTemplateWithPath,
  OcrResult
} from '../types/document-template';
import { environment } from '../../environments/environment';
import { DocumentMetadata, WebhookDelivery } from '../types/document-metadata';
import { FieldCorrection, SaveFieldCorrectionRequest } from '../types/field-correction';
import { MAX_UPLOAD_BYTES, formatBytes } from '../utils/upload.constants';
import { PresignedPost, S3UploadService, UploadEvent } from './s3-upload.service';

/** Response of `POST /documents/initiate-upload`. */
export interface InitiateUploadResponse {
  s3Key: string;
  upload: PresignedPost;
  maxBytes: number;
}

export interface UploadCallbacks {
  /** Bytes transmitted, 0–100. Does NOT mean S3 has accepted the upload. */
  onProgress?: (percent: number) => void;
  /** S3 returned 2xx — the object exists and the pipeline will pick it up. */
  onComplete?: () => void;
}

const POLL_INTERVAL_MS = 2000;

/**
 * Just past the durable function's own 15-minute ceiling. A run still going after this
 * is stuck, not slow, and the alternative is a spinner that never resolves.
 */
const POLL_DEADLINE_MS = 20 * 60 * 1000;


@Injectable({
  providedIn: 'root'
})
export class DocumentTemplateService {
  private apiUrl = environment.apiUrl;
  private templatesApiUrl = `${this.apiUrl}/document-templates`;
  private extractFieldsUrl = `${this.apiUrl}/extract-fields`;
  private initiateUploadUrl = `${this.apiUrl}/documents/initiate-upload`;
  private processingMetadataUrl = `${this.apiUrl}/processing-metadata`;
  private updateOcrResultUrl = `${this.apiUrl}/ocr-result`;
  private fieldCorrectionsUrl = `${this.apiUrl}/field-corrections`;
  private reprocessDocumentUrl = `${this.apiUrl}/reprocess-document`;
  private retryWebhookUrl = `${this.apiUrl}/retry-webhook`;

  // Template store/cache
  private templatesSubject = new BehaviorSubject<DocumentTemplateWithPath[] | null>(null);
  private templatesCache$: Observable<DocumentTemplateWithPath[]> | null = null;

  constructor(private http: HttpClient) { }

  private readonly s3Upload = inject(S3UploadService);

  getDocumentTemplates(): Observable<DocumentTemplateWithPath[]> {
    // If cache doesn't exist, create it
    if (!this.templatesCache$) {
      this.templatesCache$ = this.http.get<DocumentTemplateWithPath[]>(this.templatesApiUrl).pipe(
        tap(templates => this.templatesSubject.next(templates)),
        shareReplay(1) // Cache the result and replay for subsequent subscribers
      );
    }
    return this.templatesCache$;
  }

  getDocumentTemplateById(id: string): Observable<DocumentTemplateWithPath> {
    return this.getDocumentTemplates().pipe(
      map((templates) => {
        const template = templates.find(t => t.id === id);
        if (!template) {
          throw new Error('Template not found');
        }
        return template;
      })
    );
  }

  /**
   * Refreshes the template cache by clearing it and forcing a new fetch
   */
  refreshTemplateCache(): Observable<DocumentTemplateWithPath[]> {
    this.clearCache();
    return this.getDocumentTemplates();
  }

  /**
   * Clears the template cache
   */
  private clearCache(): void {
    this.templatesCache$ = null;
    this.templatesSubject.next(null);
  }

  createDocumentTemplate(template: DocumentTemplateAddDTO): Observable<{ documentTemplate: DocumentTemplate, uploadUrl: string }> {
    return this.http.post<{ documentTemplate: DocumentTemplate, uploadUrl: string }>(this.templatesApiUrl, template).pipe(
      tap(() => this.clearCache()) // Clear cache to force refresh on next fetch
    );
  }

  updateDocumentTemplate(id: string, template: Partial<DocumentTemplate>): Observable<DocumentTemplate> {
    return this.http.put<DocumentTemplate>(`${this.templatesApiUrl}/${id}`, template).pipe(
      tap(() => this.clearCache()) // Clear cache to force refresh on next fetch
    );
  }

  deleteDocumentTemplate(id: string): Observable<void> {
    return this.http.delete<void>(`${this.templatesApiUrl}/${id}`).pipe(
      tap(() => this.clearCache()) // Clear cache to force refresh on next fetch
    );
  }

  extractAnchors(templateId: string, anchorFieldIds?: string[]): Observable<{ message: string, templateName: string, anchors: Record<string, unknown> }> {
    return this.http.post<{ message: string, templateName: string, anchors: Record<string, unknown> }>(`${this.templatesApiUrl}/anchors`, {
      templateId,
      ...(anchorFieldIds ? { anchorFieldIds } : {})
    });
  }

  extractFieldsFromFile(formData:
    {
      fileContent: string; fileName: string; structureId: string;
    }): Observable<OcrResult> {
    return this.http.post<OcrResult>(this.extractFieldsUrl, formData);
  }

  /**
   * Uploads a document and resolves once OCR reaches a terminal state.
   *
   * The bytes go straight to S3 via a pre-signed POST, so they never pass through API
   * Gateway and are not bounded by its 10 MiB limit or Lambda's 6 MB invoke quota.
   *
   * Emits only poll results — nothing is emitted while the upload is in flight, so
   * callers that need progress must pass `callbacks`.
   */
  processDocument(file: File, callbacks?: UploadCallbacks): Observable<DocumentMetadata> {
    if (file.size > MAX_UPLOAD_BYTES) {
      return throwError(() => new Error(`File exceeds the ${formatBytes(MAX_UPLOAD_BYTES)} upload limit.`));
    }

    return this.http.post<InitiateUploadResponse>(this.initiateUploadUrl, { fileName: file.name }).pipe(
      switchMap(init =>
        this.s3Upload.upload(init.upload, file).pipe(
          tap((event: UploadEvent) => {
            if (event.type === 'progress') {
              callbacks?.onProgress?.(event.percent);
            } else {
              callbacks?.onComplete?.();
            }
          }),
          // Reaching 100% only means the body was transmitted; S3 can still reject it.
          filter((event): event is Extract<UploadEvent, { type: 'complete' }> => event.type === 'complete'),
          switchMap(() => this.pollUntilTerminal(init.s3Key))
        )
      )
    );
  }

  private pollUntilTerminal(s3Key: string): Observable<DocumentMetadata> {
    const deadline = Date.now() + POLL_DEADLINE_MS;

    return interval(POLL_INTERVAL_MS).pipe(
      // Checked on the tick rather than on the response, because `exhaustMap` emits
      // nothing while a request is outstanding and `HttpClient` has no timeout of its
      // own — a single stalled read would otherwise suspend the deadline indefinitely.
      // One deadline covers every way this can hang: a document that never appears, a
      // run that never starts, and a run that never finishes.
      map(() => {
        if (Date.now() > deadline) {
          throw new Error('Processing is taking longer than expected. Please try again.');
        }
      }),
      // exhaustMap, not switchMap: this endpoint presigns two URLs and prices the run, so
      // the terminal read is the slowest one. switchMap would let each tick cancel the
      // in-flight request, and a round-trip above POLL_INTERVAL_MS would then never land
      // a single response at all.
      exhaustMap(() => this.getProcessingMetadata(s3Key).pipe(
        // No row exists until the object lands, so a 404 is the normal answer while the
        // pipeline catches up rather than a failure.
        catchError((err: HttpErrorResponse) => err.status === 404 ? of(null) : throwError(() => err))
      )),
      filter((metadata): metadata is DocumentMetadata => metadata !== null),
      takeWhile(metadata => metadata.status !== 'done' && metadata.status !== 'error', true)
    );
  }

  getProcessingMetadata(s3Key: string): Observable<DocumentMetadata> {
    return this.http.get<DocumentMetadata>(`${this.processingMetadataUrl}`, {
      params: { s3Key }
    });
  }

  /**
   * Resolves to null instead of failing when the document cannot be read, for views that
   * render an empty state rather than surfacing the error. Callers that need to react to
   * the failure use `getProcessingMetadata`.
   */
  getProcessingMetadataOrNull(s3Key: string): Observable<DocumentMetadata | null> {
    return this.getProcessingMetadata(s3Key).pipe(catchError(() => of(null)));
  }

  updateOcrResult(
    s3Key: string,
    ocrResult: Record<string, any>,
    ocrJudgeResult?: import('../types/document-metadata').LlmJudgeResult,
    markReviewed?: boolean
  ): Observable<DocumentMetadata> {
    const payload: any = { s3Key, ocrResult };
    if (ocrJudgeResult !== undefined) {
      payload.ocrJudgeResult = ocrJudgeResult; // Always send full object if provided
    }
    if (markReviewed) {
      payload.markReviewed = true;
    }
    return this.http.put<DocumentMetadata>(`${this.updateOcrResultUrl}`, payload);
  }

  reprocessDocument(s3Key: string, templateId: string): Observable<{ s3Key: string; status: string }> {
    return this.http.post<{ s3Key: string; status: string }>(this.reprocessDocumentUrl, { s3Key, templateId });
  }

  /** Sends the document's current state to its webhook once; resolves with the recorded outcome, failed or not. */
  retryWebhook(s3Key: string): Observable<{ s3Key: string; webhookDelivery: WebhookDelivery }> {
    return this.http.post<{ s3Key: string; webhookDelivery: WebhookDelivery }>(this.retryWebhookUrl, { s3Key });
  }

  saveFieldCorrection(correction: SaveFieldCorrectionRequest): Observable<FieldCorrection> {
    return this.http.post<FieldCorrection>(this.fieldCorrectionsUrl, correction);
  }

  getFieldCorrections(documentId: string): Observable<FieldCorrection[]> {
    return this.http.get<FieldCorrection[]>(this.fieldCorrectionsUrl, {
      params: { documentId }
    });
  }
}
